import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'
import PlaylistManager from '../components/PlaylistManager'

interface Video {
  id: string; title: string; description: string; video_url: string
  thumbnail_url: string; content_type: string; duration: number
  release_year: number; created_at: string; views: number
  likes_count: number; comments_count: number
  genre?: { name_en: string; id: string }; user?: { username: string }
}
interface Comment {
  id: string; content: string; user_id: string; video_id: string
  parent_id?: string | null; created_at: string
  user?: { username: string }; replies?: Comment[]
}
interface QualityOption { id: string; quality: string; label: string; video_url: string }
interface Subtitle { id: string; language_code: string; language_name: string; subtitle_url: string }
interface User { id: string; username: string; role: string }
interface Props { currentUser: User | null }

const fmtTimeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime()
  const s = Math.floor(diff / 1000), m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000), day = Math.floor(diff / 86400000)
  const wk = Math.floor(day / 7), mo = Math.floor(day / 30), yr = Math.floor(day / 365)
  if (s < 10) return 'Just now'
  if (s < 60) return `${s}s ago`
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (day < 7) return `${day}d ago`
  if (wk < 5) return `${wk}w ago`
  if (mo < 12) return `${mo}mo ago`
  return `${yr}y ago`
}
const fmtNum = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
  : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`

const TYPE_COLOR: Record<string, string> = {
  movie: '#c084fc', series: '#60a5fa', short_drama: '#fb923c', podcast: '#34d399'
}

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar: React.FC<{ name?: string; size?: number; bg?: string }> = ({
  name, size = 32, bg = 'linear-gradient(135deg,#e53935,#8e24aa)'
}) => (
  <div style={{
    width: size, height: size, borderRadius: size / 2, flexShrink: 0,
    background: bg, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: size * 0.38, fontWeight: 800, color: '#fff',
  }}>
    {name?.charAt(0).toUpperCase() || '?'}
  </div>
)

// ── Star Rating ───────────────────────────────────────────────────────────────
const StarRating: React.FC<{ rating: number; onRate: (rating: number) => void; isDark: boolean }> = ({ rating, onRate, isDark }) => {
  const [hover, setHover] = useState(0)
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onClick={() => onRate(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          style={{
            background: 'none', border: 'none',
            fontSize: 'clamp(16px, 3vw, 20px)', cursor: 'pointer', padding: 'clamp(4px, 1vw, 8px)',
            color: star <= (hover || rating) ? '#f59e0b' : isDark ? '#444' : '#d1d5db',
            transition: 'color .15s', minWidth: 'clamp(32px, 6vw, 40px)',
          }}
        >
          ★
        </button>
      ))}
    </div>
  )
}

// ── CommentNode ───────────────────────────────────────────────────────────────
const CommentNode: React.FC<{
  comment: Comment; depth: number
  isDark: boolean; textColor: string; subColor: string; borderColor: string
  onReply: (id: string, username: string) => void
}> = ({ comment, depth, isDark, textColor, subColor, borderColor, onReply }) => {
  const [showReplies, setShowReplies] = useState(false)
  const replyCount = comment.replies?.length || 0

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        paddingLeft: depth > 0 ? 'clamp(8px, 2vw, 12px)' : 0,
        borderLeft: depth > 0 ? '2px solid rgba(229,57,53,.2)' : 'none',
        marginLeft: depth > 0 ? 'clamp(8px, 2vw, 16px)' : 0,
      }}>
        <div style={{ display: 'flex', gap: 'clamp(6px, 1.5vw, 10px)', alignItems: 'flex-start' }}>
          <Avatar name={comment.user?.username} size={depth > 0 ? 'clamp(22px, 4vw, 26px)' as any : 'clamp(26px, 5vw, 30px)' as any} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
              <span style={{ color: textColor, fontSize: 'clamp(11px, 2vw, 13px)', fontWeight: 700 }}>
                {comment.user?.username || 'Guest'}
              </span>
              <span style={{ color: subColor, fontSize: 'clamp(10px, 1.8vw, 11px)' }}>{fmtTimeAgo(comment.created_at)}</span>
              {depth > 0 && (
                <span style={{ padding: '1px 6px', borderRadius: 5, fontSize: 9, fontWeight: 800, background: 'rgba(96,165,250,.15)', color: '#60a5fa' }}>
                  REPLY
                </span>
              )}
            </div>
            <p style={{ color: isDark ? '#ccc' : '#475569', fontSize: 'clamp(12px, 2vw, 13px)', lineHeight: 1.6, margin: '0 0 8px', wordBreak: 'break-word' }}>
              {comment.content}
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => onReply(comment.id, comment.user?.username || '')}
                style={{ background: 'none', border: 'none', color: subColor, fontSize: 'clamp(10px, 1.8vw, 11px)', fontWeight: 600, cursor: 'pointer', padding: '4px 0', transition: 'color .15s' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#e53935' }}
                onMouseLeave={e => { e.currentTarget.style.color = subColor }}
              >
                ↩ Reply
              </button>
              {replyCount > 0 && depth === 0 && (
                <button
                  onClick={() => setShowReplies(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 'clamp(10px, 1.8vw, 11px)', fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}
                >
                  {showReplies ? '▼' : '▶'} {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </button>
              )}
            </div>
          </div>
        </div>
        {showReplies && comment.replies && comment.replies.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {comment.replies.map(r => (
              <CommentNode key={r.id} comment={r} depth={depth + 1}
                isDark={isDark} textColor={textColor} subColor={subColor}
                borderColor={borderColor} onReply={onReply} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function VideoDetailScreen({ currentUser }: Props) {
  const { id: videoId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isDark } = useTheme()
  const isAnon = !currentUser
  const videoRef = useRef<HTMLVideoElement>(null)
  const viewCountedRef = useRef(false)
  const currentUserRef = useRef(currentUser)
  useEffect(() => { currentUserRef.current = currentUser }, [currentUser])

  const [video, setVideo] = useState<Video | null>(null)
  const [loading, setLoading] = useState(true)
  const [liked, setLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [liking, setLiking] = useState(false)
  const [userRating, setUserRating] = useState(0)
  const [averageRating, setAverageRating] = useState(0)
  const [ratingCount, setRatingCount] = useState(0)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentCount, setCommentCount] = useState(0)
  const [newComment, setNewComment] = useState('')
  const [posting, setPosting] = useState(false)
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyToUsername, setReplyToUsername] = useState('')
  const [commentFocused, setCommentFocused] = useState(false)
  const [qualityOptions, setQualityOptions] = useState<QualityOption[]>([])
  const [subtitles, setSubtitles] = useState<Subtitle[]>([])
  const [activeQuality, setActiveQuality] = useState<QualityOption | null>(null)
  const [activeSubtitle, setActiveSubtitle] = useState<Subtitle | null>(null)
  const [showAuth, setShowAuth] = useState(false)

  // Player state
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showQSInline, setShowQSInline] = useState(false)
  const [qsTab, setQsTab] = useState<'quality' | 'subtitle'>('quality')
  const [isPortraitVideo, setIsPortraitVideo] = useState(false)
  const playerRef = useRef<HTMLDivElement>(null)
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // @mention
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionResults, setMentionResults] = useState<{ id: string; username: string }[]>([])
  const [showMentions, setShowMentions] = useState(false)
  const [mentionStart, setMentionStart] = useState(-1)
  const commentInputRef = useRef<HTMLTextAreaElement>(null)
  const [relatedVideos, setRelatedVideos] = useState<any[]>([])

  const C = {
    bg: isDark ? '#080810' : '#f0f2f8',
    panel: isDark ? 'rgba(255,255,255,.03)' : '#fff',
    border: isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.07)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#555' : '#94a3b8',
    inputBg: isDark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.04)',
  }

  // Detect video orientation
  const detectVideoOrientation = (videoElement: HTMLVideoElement) => {
    if (videoElement.videoWidth && videoElement.videoHeight) {
      setIsPortraitVideo(videoElement.videoHeight > videoElement.videoWidth)
    }
  }

  useEffect(() => {
    if (!videoId) return
    viewCountedRef.current = false
    setLoading(true)
    setVideo(null)
    setLiked(false)
    const load = async () => {
      await Promise.all([fetchVideo(), fetchComments(), fetchQS()])
      await checkLiked()
      if (currentUser) await checkRating()
      await fetchAverageRating()
      await fetchRelatedVideos()
    }
    load()
  }, [videoId])

  useEffect(() => {
    if (video) {
      setLikeCount(video.likes_count || 0)
      setCommentCount(video.comments_count || 0)
    }
  }, [video?.id])

  useEffect(() => {
    const vid = videoRef.current
    if (!vid || !videoId) return
    const onTime = () => {
      if (viewCountedRef.current) return
      if (!vid.duration || vid.duration === 0) return
      if ((vid.currentTime / vid.duration) * 100 >= 50) {
        viewCountedRef.current = true
        vid.removeEventListener('timeupdate', onTime)
        incrementView()
      }
    }
    vid.addEventListener('timeupdate', onTime)
    return () => {
      vid.removeEventListener('timeupdate', onTime)
    }
  }, [videoId])

  useEffect(() => {
    return () => { recordWatchHistory() }
  }, [videoId])

  // Fullscreen change listener
  React.useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    document.addEventListener('webkitfullscreenchange', handler)
    return () => {
      document.removeEventListener('fullscreenchange', handler)
      document.removeEventListener('webkitfullscreenchange', handler)
    }
  }, [])

  // Fetch functions (same as before but with responsive adjustments)
  const fetchVideo = async () => {
    const { data, error } = await supabase
      .from('videos').select('*, genre:genres(name_en, id), user:profiles(username)')
      .eq('id', videoId!).single()
    if (!error && data) setVideo(data)
    else if (error) console.error('Error fetching video:', error)
    setLoading(false)
  }

  const fetchComments = async () => {
    const { data, error } = await supabase
      .from('comments').select('*, user:profiles(username)')
      .eq('video_id', videoId!).order('created_at', { ascending: true })
    if (!error && data) {
      const map = new Map<string, Comment>()
      data.forEach(c => map.set(c.id, { ...c, replies: [] }))
      const roots: Comment[] = []
      data.forEach(c => {
        if (c.parent_id && map.has(c.parent_id)) map.get(c.parent_id)!.replies!.push(map.get(c.id)!)
        else roots.push(map.get(c.id)!)
      })
      setComments(roots)
      setCommentCount(data.length)
    }
  }

  const fetchQS = async () => {
    const [{ data: q }, { data: s }] = await Promise.all([
      supabase.from('video_quality_options').select('*').eq('video_id', videoId!),
      supabase.from('video_subtitles').select('*').eq('video_id', videoId!),
    ])
    if (q) setQualityOptions(q)
    if (s) setSubtitles(s)
  }

  const checkLiked = async () => {
    if (currentUser) {
      const { data } = await supabase.from('likes').select('id').eq('video_id', videoId!).eq('user_id', currentUser.id).maybeSingle()
      setLiked(!!data)
    } else {
      const deviceId = localStorage.getItem('vs_device_id')
      if (!deviceId) { setLiked(false); return }
      const { data } = await supabase.from('likes').select('id').eq('video_id', videoId!).eq('device_id', deviceId).maybeSingle()
      setLiked(!!data)
    }
  }

  const checkRating = async () => {
    if (!currentUser) return
    const { data } = await supabase.from('ratings').select('rating').eq('video_id', videoId!).eq('user_id', currentUser.id).single()
    if (data) setUserRating(data.rating)
  }

  const fetchAverageRating = async () => {
    const { data, count } = await supabase.from('ratings').select('rating', { count: 'exact' }).eq('video_id', videoId!)
    if (data && data.length > 0) {
      setAverageRating(Math.round((data.reduce((s, r) => s + r.rating, 0) / data.length) * 10) / 10)
      setRatingCount(count || 0)
    }
  }

  const fetchRelatedVideos = async () => {
    if (!video) return
    const { data } = await supabase.from('videos')
      .select('id, title, thumbnail_url, content_type, duration, views')
      .eq('is_public', true).eq('genre_id', video.genre?.id).neq('id', videoId!)
      .order('views', { ascending: false }).limit(6)
    if (data && data.length > 0) setRelatedVideos(data)
    else {
      const { data: popular } = await supabase.from('videos')
        .select('id, title, thumbnail_url, content_type, duration, views')
        .eq('is_public', true).neq('id', videoId!).order('views', { ascending: false }).limit(6)
      setRelatedVideos(popular || [])
    }
  }

  const searchMentions = async (query: string) => {
    if (!query) { setMentionResults([]); return }
    const { data } = await supabase.from('profiles').select('id, username').ilike('username', `${query}%`).neq('id', currentUser?.id || '').limit(6)
    setMentionResults(data || [])
  }

  const handleCommentInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value; setNewComment(val)
    const cursor = e.target.selectionStart
    const atIdx = val.slice(0, cursor).lastIndexOf('@')
    if (atIdx !== -1) {
      const between = val.slice(atIdx + 1, cursor)
      if (!between.includes(' ') && between.length <= 20) {
        setMentionStart(atIdx); setMentionQuery(between); setShowMentions(true); searchMentions(between); return
      }
    }
    setShowMentions(false); setMentionQuery(''); setMentionStart(-1)
  }

  const insertMention = (username: string) => {
    if (mentionStart === -1) return
    const before = newComment.slice(0, mentionStart)
    const after = newComment.slice(mentionStart + 1 + mentionQuery.length)
    setNewComment(`${before}@${username} ${after}`)
    setShowMentions(false); setMentionQuery(''); setMentionStart(-1)
    setTimeout(() => commentInputRef.current?.focus(), 50)
  }

  const fireMentionNotifications = async (commentId: string, content: string) => {
    if (!currentUser || !videoId || !/@[A-Za-z0-9_]+/.test(content)) return
    try { await supabase.rpc('create_mention_notifications', { p_comment_id: commentId, p_video_id: videoId, p_from_user_id: currentUser.id, p_content: content }) } catch (e) { console.error('Mention error:', e) }
  }

  const recordWatchHistory = async () => {
    if (!videoId) return
    try {
      let deviceId = localStorage.getItem('vs_device_id')
      if (!deviceId) { deviceId = 'web_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('vs_device_id', deviceId) }
      await supabase.from('watch_history').insert({
        user_id: currentUser?.id ?? null, device_id: currentUser ? null : deviceId,
        video_id: videoId, watch_duration: videoRef.current ? Math.floor(videoRef.current.currentTime) : 0,
        completed: videoRef.current && video?.duration ? (videoRef.current.currentTime / video.duration) >= 0.9 : false,
        watched_at: new Date().toISOString()
      })
    } catch (e) { console.error('Watch history error:', e) }
  }

  const incrementView = async () => {
    if (!videoId) return
    try {
      let deviceId = localStorage.getItem('vs_device_id')
      if (!deviceId) { deviceId = 'web_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('vs_device_id', deviceId) }
      const { data, error } = await supabase.rpc('increment_video_views', { p_video_id: videoId, p_user_id: currentUserRef.current?.id ?? null, p_device_id: currentUserRef.current ? null : deviceId })
      if (!error && data?.view_count) setVideo(v => v ? { ...v, views: data.view_count } : v)
    } catch (e) { console.error('incrementView error:', e) }
  }

  const handleLike = async () => {
    if (liking) return
    setLiking(true)
    const prevLiked = liked, prevCount = likeCount
    setLiked(!prevLiked); setLikeCount(prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1)
    try {
      let deviceId = localStorage.getItem('vs_device_id')
      if (!deviceId) { deviceId = 'web_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('vs_device_id', deviceId) }
      if (currentUser) {
        if (prevLiked) await supabase.from('likes').delete().eq('video_id', videoId!).eq('user_id', currentUser.id)
        else await supabase.from('likes').insert({ video_id: videoId, user_id: currentUser.id, device_id: null })
      } else {
        if (prevLiked) await supabase.from('likes').delete().eq('video_id', videoId!).eq('device_id', deviceId)
        else await supabase.from('likes').insert({ video_id: videoId, user_id: null, device_id: deviceId })
      }
      const { count } = await supabase.from('likes').select('*', { count: 'exact', head: true }).eq('video_id', videoId!)
      if (count !== null) setLikeCount(count)
    } catch (e) { setLiked(prevLiked); setLikeCount(prevCount) }
    finally { setLiking(false) }
  }

  const handleRate = async (rating: number) => {
    if (!currentUser) { setShowAuth(true); return }
    const prev = userRating; setUserRating(rating)
    try {
      if (prev > 0) await supabase.from('ratings').update({ rating }).eq('video_id', videoId!).eq('user_id', currentUser.id)
      else await supabase.from('ratings').insert({ video_id: videoId, user_id: currentUser.id, rating })
      await fetchAverageRating()
    } catch (e) { setUserRating(prev) }
  }

  const handleComment = async () => {
    if (!newComment.trim() || posting) return
    setPosting(true)
    try {
      const { data: inserted, error } = await supabase.from('comments').insert([{ content: newComment.trim(), video_id: videoId, user_id: currentUser?.id ?? null, parent_id: replyTo || null }]).select('id').single()
      if (error) { alert('Failed to post: ' + error.message); return }
      setNewComment(''); setReplyTo(null); setReplyToUsername(''); setCommentFocused(false); setShowMentions(false)
      if (inserted?.id) fireMentionNotifications(inserted.id, newComment.trim())
      await fetchComments()
    } catch (e: any) { alert('Error: ' + (e?.message || 'Unknown')) }
    finally { setPosting(false) }
  }

  const handleReply = (id: string, username: string) => { setReplyTo(id); setReplyToUsername(username); setTimeout(() => commentInputRef.current?.focus(), 100) }

  const fmtTime = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60)
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
  }

  const fmtDuration = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return
    if (v.paused) { v.play().catch(() => {}); setIsPlaying(true) }
    else { v.pause(); setIsPlaying(false) }
  }

  const toggleMute = () => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setIsMuted(v.muted) }
  const handleVolumeChange = (val: number) => { const v = videoRef.current; if (!v) return; v.volume = val; setVolume(val); v.muted = val === 0; setIsMuted(val === 0) }
  const handleSeek = (val: number) => { const v = videoRef.current; if (!v) return; v.currentTime = val; setCurrentTime(val) }
  const skip = (secs: number) => { const v = videoRef.current; if (!v) return; v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + secs)) }

  const toggleFullscreen = () => {
    const el = playerRef.current; if (!el) return
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }

  const resetControlsTimer = () => {
    setShowControls(true)
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current)
    controlsTimerRef.current = setTimeout(() => { if (isPlaying) setShowControls(false) }, 3000)
  }

  const handleQuality = (q: QualityOption) => {
    const t = videoRef.current?.currentTime || 0
    const wasPlaying = !videoRef.current?.paused
    setActiveQuality(q)
    setTimeout(() => {
      if (videoRef.current) { videoRef.current.currentTime = t; if (wasPlaying) videoRef.current.play().catch(() => {}) }
    }, 300)
  }

  const videoSrc = activeQuality?.video_url || video?.video_url || ''
  const typeColor = TYPE_COLOR[video?.content_type || ''] || '#e53935'

  if (loading || !video) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 40, height: 40, border: '2px solid #e53935', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: C.bg, overflowX: 'hidden' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes _panelIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes _fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .vd-like:not(:disabled):hover{transform:scale(1.06)}
        input[type=range]{height:4px;border-radius:4px;outline:none;-webkit-appearance:none;appearance:none;background:rgba(255,255,255,.2)}
        input[type=range]::-webkit-slider-thumb{width:clamp(12px,3vw,16px);height:clamp(12px,3vw,16px);border-radius:50%;cursor:pointer;background:#fff;-webkit-appearance:none;appearance:none}
        
        @media (max-width: 860px) {
          .vd-layout{grid-template-columns:1fr !important;padding:0 12px !important;gap:16px !important}
          .vd-sidebar{position:static !important;max-height:none !important;margin-top:20px}
          .vd-player{max-height:50vh !important;border-radius:0 !important;margin:0 -12px !important}
          .vd-info{padding:0 4px !important}
          .vd-actions{flex-wrap:wrap !important;gap:8px !important}
          .vd-actions button,.vd-actions > div{flex:1 !important;min-width:80px !important;justify-content:center !important;font-size:12px !important;padding:8px 12px !important}
          .vd-title{font-size:clamp(18px,4vw,22px) !important}
          .related-grid{grid-template-columns:repeat(2,1fr) !important;gap:10px !important}
        }
        
        @media (max-width: 480px) {
          .vd-layout{padding:0 8px !important}
          .vd-player{max-height:40vh !important;margin:0 -8px !important}
          .vd-player.portrait{max-height:60vh !important}
          .vd-actions button,.vd-actions > div{font-size:11px !important;padding:6px 10px !important}
          .related-grid{grid-template-columns:1fr !important}
          .vd-title{font-size:clamp(16px,3.5vw,20px) !important}
          .vd-meta span{font-size:11px !important}
          .comment-section{padding:8px !important}
        }
      `}</style>

      {showAuth && (
        <div onClick={() => setShowAuth(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDark ? '#111318' : '#fff', borderRadius: 'clamp(16px,3vw,24px)', maxWidth: 440, width: '100%', border: `1px solid ${C.border}`, animation: '_panelIn .3s ease' }}>
            <div style={{ height: 4, background: 'linear-gradient(90deg,#e53935,#8e24aa)' }} />
            <div style={{ padding: 'clamp(20px,4vw,32px)', textAlign: 'center' }}>
              <div style={{ fontSize: 'clamp(36px,6vw,44px)', marginBottom: 12 }}>🔐</div>
              <h3 style={{ color: C.text, fontSize: 'clamp(16px,3vw,20px)', fontWeight: 800, marginBottom: 8 }}>Sign in required</h3>
              <p style={{ color: C.sub, fontSize: 'clamp(12px,2vw,14px)', marginBottom: 24, lineHeight: 1.5 }}>You need an account to access all features.</p>
              <button onClick={() => { setShowAuth(false); navigate('/login') }} style={{ width: '100%', padding: 'clamp(12px,2vw,14px) 0', borderRadius: 14, border: 'none', background: 'linear-gradient(90deg,#e53935,#8e24aa)', color: '#fff', fontSize: 'clamp(14px,2.5vw,16px)', fontWeight: 700, cursor: 'pointer', marginBottom: 10, minHeight: 44 }}>Sign In / Sign Up</button>
              <button onClick={() => setShowAuth(false)} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 'clamp(12px,2vw,14px)', cursor: 'pointer' }}>Continue browsing</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="vd-layout" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(12px,3vw,24px) clamp(40px,8vw,64px)', display: 'grid', gridTemplateColumns: '1fr 380px', gap: 'clamp(16px,3vw,28px)', alignItems: 'start' }}>

        {/* Left Column */}
        <div style={{ minWidth: 0 }}>
          <div className="vd-info" style={{ padding: 'clamp(8px,2vw,16px) 0 clamp(8px,2vw,12px)' }}>
            <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: C.sub, fontSize: 'clamp(12px,2vw,14px)', fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Back
            </button>
          </div>

          {/* Video Player */}
          <div
            ref={playerRef}
            className={`vd-player ${isPortraitVideo ? 'portrait' : ''}`}
            onMouseMove={resetControlsTimer}
            onMouseLeave={() => { if (isPlaying) setShowControls(false) }}
            onMouseEnter={() => setShowControls(true)}
            onTouchStart={() => { resetControlsTimer(); togglePlay() }}
            style={{
              position: 'relative',
              borderRadius: isFullscreen ? 0 : 'clamp(12px,2vw,18px)',
              overflow: 'hidden',
              background: '#000',
              boxShadow: isFullscreen ? 'none' : `0 0 50px ${typeColor}18, 0 20px 60px rgba(0,0,0,.5)`,
              cursor: 'pointer',
              userSelect: 'none',
              maxHeight: isFullscreen ? '100vh' : (isPortraitVideo ? 'clamp(400px,70vh,600px)' : 'clamp(300px,55vh,520px)'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!isFullscreen && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${typeColor},transparent)`, zIndex: 10 }} />}

            <video
              key={videoSrc}
              ref={videoRef}
              onContextMenu={e => e.preventDefault()}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
              onLoadedMetadata={e => {
                setDuration(e.currentTarget.duration)
                detectVideoOrientation(e.currentTarget)
              }}
              onVolumeChange={e => { setIsMuted(e.currentTarget.muted); setVolume(e.currentTarget.volume) }}
              playsInline
              webkit-playsinline="true"
              x-webkit-airplay="allow"
              style={{
                width: '100%',
                height: isFullscreen ? '100vh' : 'auto',
                maxHeight: isFullscreen ? '100vh' : (isPortraitVideo ? 'clamp(400px,70vh,600px)' : 'clamp(300px,55vh,520px)'),
                display: 'block',
                background: '#000',
                objectFit: isFullscreen ? 'contain' : 'contain',
              }}
            >
              <source src={videoSrc} />
              {activeSubtitle && <track kind="subtitles" src={activeSubtitle.subtitle_url} srcLang={activeSubtitle.language_code} label={activeSubtitle.language_name} default />}
            </video>

            {/* Play button overlay */}
            {!isPlaying && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 8, pointerEvents: 'none' }}>
                <div style={{ width: 'clamp(48px,10vw,68px)', height: 'clamp(48px,10vw,68px)', borderRadius: '50%', background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255,255,255,.3)' }}>
                  <svg width="clamp(20px,4vw,28px)" height="clamp(20px,4vw,28px)" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </div>
            )}

            {/* QS Panel */}
            {showQSInline && (
              <div className="player-control" onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 'clamp(50px,8vw,70px)', right: 'clamp(8px,2vw,12px)', background: 'rgba(10,10,20,.95)', backdropFilter: 'blur(20px)', borderRadius: 16, border: '1px solid rgba(255,255,255,.12)', padding: 'clamp(8px,2vw,12px)', minWidth: 'clamp(180px,40vw,220px)', zIndex: 30, boxShadow: '0 8px 40px rgba(0,0,0,.6)' }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                  {([['quality','📺 Quality'],['subtitle','💬 Subtitles']] as [typeof qsTab, string][]).filter(([tab]) => tab === 'quality' ? qualityOptions.length > 0 : subtitles.length > 0).map(([tab, label]) => (
                    <button key={tab} onClick={() => setQsTab(tab)} className="player-control" style={{ flex: 1, padding: 'clamp(5px,1vw,6px) clamp(8px,2vw,10px)', borderRadius: 10, border: 'none', cursor: 'pointer', background: qsTab === tab ? 'rgba(229,57,53,.25)' : 'rgba(255,255,255,.08)', color: qsTab === tab ? '#e53935' : '#ccc', fontSize: 'clamp(10px,2vw,11px)', fontWeight: 700 }}>{label}</button>
                  ))}
                </div>
                {/* Quality/Subtitle lists (same as before, responsive) */}
                {qsTab === 'quality' && qualityOptions.length > 0 && (
                  <div>
                    <button onClick={() => { setActiveQuality(null); setShowQSInline(false) }} className="player-control" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: 'clamp(6px,1.5vw,8px) clamp(8px,2vw,10px)', borderRadius: 9, border: 'none', cursor: 'pointer', background: !activeQuality ? 'rgba(229,57,53,.15)' : 'transparent', color: !activeQuality ? '#e53935' : '#ccc', fontSize: 'clamp(11px,2vw,13px)', marginBottom: 4 }}><span>Auto</span>{!activeQuality && <span>✓</span>}</button>
                    {qualityOptions.map(q => (
                      <button key={q.id} onClick={() => { handleQuality(q); setShowQSInline(false) }} className="player-control" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: 'clamp(6px,1.5vw,8px) clamp(8px,2vw,10px)', borderRadius: 9, border: 'none', cursor: 'pointer', background: activeQuality?.id === q.id ? 'rgba(59,130,246,.15)' : 'transparent', color: activeQuality?.id === q.id ? '#60a5fa' : '#ccc', fontSize: 'clamp(11px,2vw,13px)', marginBottom: 4 }}><span>{q.label}</span>{activeQuality?.id === q.id && <span>✓</span>}</button>
                    ))}
                  </div>
                )}
                {qsTab === 'subtitle' && subtitles.length > 0 && (
                  <div>
                    <button onClick={() => { setActiveSubtitle(null); setShowQSInline(false) }} className="player-control" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: 'clamp(6px,1.5vw,8px) clamp(8px,2vw,10px)', borderRadius: 9, border: 'none', cursor: 'pointer', background: !activeSubtitle ? 'rgba(229,57,53,.15)' : 'transparent', color: !activeSubtitle ? '#e53935' : '#ccc', fontSize: 'clamp(11px,2vw,13px)', marginBottom: 4 }}><span>Off</span>{!activeSubtitle && <span>✓</span>}</button>
                    {subtitles.map(s => (
                      <button key={s.id} onClick={() => { setActiveSubtitle(s); setShowQSInline(false) }} className="player-control" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: 'clamp(6px,1.5vw,8px) clamp(8px,2vw,10px)', borderRadius: 9, border: 'none', cursor: 'pointer', background: activeSubtitle?.id === s.id ? 'rgba(34,197,94,.15)' : 'transparent', color: activeSubtitle?.id === s.id ? '#22c55e' : '#ccc', fontSize: 'clamp(11px,2vw,13px)', marginBottom: 4 }}><span>{s.language_name}</span>{activeSubtitle?.id === s.id && <span>✓</span>}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Controls Bar */}
            <div className="player-control" onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 'clamp(16px,3vw,20px) clamp(8px,2vw,12px) clamp(6px,1.5vw,10px)', background: 'linear-gradient(to top, rgba(0,0,0,.9) 0%, transparent 100%)', transition: 'opacity .3s ease', opacity: showControls ? 1 : 0, zIndex: 20, pointerEvents: showControls ? 'auto' : 'none' }}>
              <div style={{ marginBottom: 'clamp(4px,1vw,8px)', position: 'relative', height: 'clamp(3px,0.8vw,4px)', cursor: 'pointer' }} onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); handleSeek(((e.clientX - rect.left) / rect.width) * duration) }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.2)', borderRadius: 4 }} />
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`, background: `linear-gradient(90deg,${typeColor},#e53935)`, borderRadius: 4 }} />
                <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`, width: 'clamp(10px,2vw,12px)', height: 'clamp(10px,2vw,12px)', borderRadius: '50%', background: '#fff', marginLeft: -6, boxShadow: '0 0 6px rgba(0,0,0,.5)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px,1vw,8px)' }}>
                <button onClick={togglePlay} className="player-control" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(2px,1vw,4px) clamp(4px,1vw,6px)', display: 'flex', alignItems: 'center', minWidth: 'clamp(32px,6vw,40px)', minHeight: 'clamp(32px,6vw,40px)', justifyContent: 'center' }}>
                  {isPlaying
                    ? <svg width="clamp(16px,3vw,18px)" height="clamp(16px,3vw,18px)" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                    : <svg width="clamp(16px,3vw,18px)" height="clamp(16px,3vw,18px)" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                  }
                </button>
                <button onClick={() => skip(-10)} className="player-control" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(2px,1vw,4px) clamp(4px,1vw,6px)', fontSize: 'clamp(10px,2vw,11px)', fontWeight: 700, minWidth: 'clamp(28px,5vw,36px)', minHeight: 'clamp(28px,5vw,36px)' }}>-10</button>
                <button onClick={() => skip(10)} className="player-control" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(2px,1vw,4px) clamp(4px,1vw,6px)', fontSize: 'clamp(10px,2vw,11px)', fontWeight: 700, minWidth: 'clamp(28px,5vw,36px)', minHeight: 'clamp(28px,5vw,36px)' }}>+10</button>
                <button onClick={toggleMute} className="player-control" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(2px,1vw,4px)', fontSize: 'clamp(14px,3vw,16px)', minWidth: 'clamp(32px,6vw,40px)', minHeight: 'clamp(32px,6vw,40px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}</button>
                <input type="range" min={0} max={1} step={0.05} value={isMuted ? 0 : volume} onChange={e => handleVolumeChange(Number(e.target.value))} className="player-control" style={{ width: 'clamp(50px,10vw,70px)', accentColor: typeColor, cursor: 'pointer' }} />
                <span style={{ color: 'rgba(255,255,255,.8)', fontSize: 'clamp(9px,2vw,11px)', fontWeight: 600, marginLeft: 4, whiteSpace: 'nowrap' }}>{fmtTime(currentTime)} / {fmtTime(duration)}</span>
                <div style={{ flex: 1 }} />
                {(qualityOptions.length > 0 || subtitles.length > 0) && (
                  <button onClick={() => { setShowQSInline(v => !v); setQsTab(qualityOptions.length > 0 ? 'quality' : 'subtitle') }} className="player-control" style={{ background: showQSInline ? 'rgba(229,57,53,.25)' : 'rgba(255,255,255,.1)', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(4px,1vw,5px) clamp(6px,2vw,8px)', borderRadius: 8, fontSize: 'clamp(12px,2.5vw,14px)', minWidth: 'clamp(32px,6vw,40px)', minHeight: 'clamp(32px,6vw,40px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⚙</button>
                )}
                <button onClick={toggleFullscreen} className="player-control" style={{ background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff', cursor: 'pointer', padding: 'clamp(4px,1vw,5px) clamp(6px,2vw,8px)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 'clamp(32px,6vw,40px)', minHeight: 'clamp(32px,6vw,40px)' }}>
                  <svg width="clamp(14px,2.5vw,16px)" height="clamp(14px,2.5vw,16px)" viewBox="0 0 24 24" fill="white">
                    {isFullscreen
                      ? <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                      : <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                    }
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Video Info */}
          <div className="vd-info" style={{ marginTop: 'clamp(12px,3vw,18px)', animation: '_fadeUp .4s ease' }}>
            <span style={{ display: 'inline-block', padding: 'clamp(2px,0.5vw,3px) clamp(8px,2vw,10px)', borderRadius: 7, marginBottom: 10, background: typeColor + '22', border: `1px solid ${typeColor}44`, color: typeColor, fontSize: 'clamp(9px,1.5vw,10px)', fontWeight: 800, letterSpacing: '1px' }}>{video.content_type.replace('_', ' ').toUpperCase()}</span>
            <h1 className="vd-title" style={{ color: C.text, fontSize: 'clamp(18px,3vw,26px)', fontWeight: 900, lineHeight: 1.2, marginBottom: 12, fontFamily: "'Syne',sans-serif", letterSpacing: '-.3px' }}>{video.title}</h1>

            <div className="vd-meta" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px,2vw,14px)', flexWrap: 'wrap', marginBottom: 16 }}>
              <span style={{ color: C.sub, fontSize: 'clamp(11px,2vw,13px)' }}>👁 {(video.views || 0).toLocaleString()} views</span>
              <span style={{ color: C.sub, fontSize: 'clamp(11px,2vw,13px)' }}>🕐 {fmtTimeAgo(video.created_at)}</span>
              {video.release_year > 0 && <span style={{ color: C.sub, fontSize: 'clamp(11px,2vw,13px)' }}>🗓 {video.release_year}</span>}
              {video.genre && <span style={{ color: typeColor, fontSize: 'clamp(11px,2vw,13px)', fontWeight: 600 }}>● {video.genre.name_en}</span>}
              {ratingCount > 0 && <span style={{ color: '#f59e0b', fontSize: 'clamp(11px,2vw,13px)', fontWeight: 600 }}>★ {averageRating} ({ratingCount})</span>}
            </div>

            <div style={{ marginBottom: 16 }}>
              <p style={{ color: C.sub, fontSize: 'clamp(10px,1.8vw,11px)', fontWeight: 600, marginBottom: 6 }}>{currentUser ? 'Rate this video:' : 'Sign in to rate'}</p>
              <StarRating rating={userRating} onRate={handleRate} isDark={isDark} />
            </div>

            <div className="vd-actions" style={{ display: 'flex', gap: 'clamp(6px,1.5vw,10px)', padding: 'clamp(10px,2vw,14px) 0', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, marginBottom: 18, flexWrap: 'wrap' }}>
              <button className="vd-like" onClick={handleLike} disabled={liking} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 'clamp(8px,1.5vw,10px) clamp(14px,2.5vw,20px)', borderRadius: 24, cursor: liking ? 'not-allowed' : 'pointer', border: liked ? '1px solid rgba(229,57,53,.5)' : `1px solid ${C.border}`, background: liked ? 'rgba(229,57,53,.12)' : 'transparent', color: liked ? '#e53935' : C.sub, fontSize: 'clamp(12px,2vw,14px)', fontWeight: 700, transition: 'all .2s', minHeight: 'clamp(36px,6vw,44px)' }}>
                <span style={{ fontSize: 'clamp(16px,3vw,18px)' }}>{liked ? '❤️' : '🤍'}</span><span>{fmtNum(likeCount)}</span>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 'clamp(8px,1.5vw,10px) clamp(14px,2.5vw,20px)', borderRadius: 24, border: `1px solid ${C.border}`, color: C.sub, fontSize: 'clamp(12px,2vw,14px)', fontWeight: 600, minHeight: 'clamp(36px,6vw,44px)' }}>
                <span style={{ fontSize: 'clamp(16px,3vw,18px)' }}>💬</span><span>{fmtNum(commentCount)}</span>
              </div>
              <button className="vd-act" onClick={async () => { try { await navigator.share({ title: video.title, url: window.location.href }) } catch { navigator.clipboard.writeText(window.location.href).catch(() => {}) } }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 'clamp(8px,1.5vw,10px) clamp(14px,2.5vw,20px)', borderRadius: 24, border: `1px solid ${C.border}`, background: 'transparent', color: C.sub, fontSize: 'clamp(12px,2vw,14px)', fontWeight: 600, cursor: 'pointer', minHeight: 'clamp(36px,6vw,44px)' }}>
                <span style={{ fontSize: 'clamp(16px,3vw,18px)' }}>📤</span><span>Share</span>
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: video.description ? 14 : 0 }}>
              <Avatar name={video.user?.username} size={42} bg={`linear-gradient(135deg,${typeColor},${typeColor}88)`} />
              <div><p style={{ color: C.text, fontSize: 14, fontWeight: 700, margin: 0 }}>{video.user?.username || 'Unknown'}</p><p style={{ color: C.sub, fontSize: 12, margin: '2px 0 0' }}>Content Creator</p></div>
            </div>
            {video.description && <div style={{ marginTop: 14, padding: 16, background: C.panel, borderRadius: 13, border: `1px solid ${C.border}` }}><p style={{ color: isDark ? '#aaa' : '#475569', fontSize: 14, lineHeight: 1.7, margin: 0 }}>{video.description}</p></div>}
            {currentUser && <PlaylistManager videoId={videoId} userId={currentUser.id} isDark={isDark} />}
            {relatedVideos.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h3 style={{ color: C.text, fontSize: 'clamp(14px,2.5vw,16px)', fontWeight: 800, marginBottom: 16, fontFamily: "'Syne',sans-serif" }}>🔗 Related Videos</h3>
                <div className="related-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(140px,25vw,180px), 1fr))', gap: 12 }}>
                  {relatedVideos.map(rv => (
                    <div key={rv.id} onClick={() => navigate(`/video/${rv.id}`)} style={{ cursor: 'pointer', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, transition: 'transform .2s', background: '#000' }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                        <img src={rv.thumbnail_url} alt={rv.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.9) 0%, rgba(0,0,0,.2) 40%, transparent 70%)' }} />
                        <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>
                          <p style={{ color: '#fff', fontSize: 'clamp(10px,2vw,12px)', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rv.title}</p>
                          <p style={{ color: 'rgba(255,255,255,.5)', fontSize: 'clamp(9px,1.5vw,10px)', margin: '2px 0 0' }}>{fmtDuration(rv.duration)} · 👁 {rv.views?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Comments */}
        <div className="vd-sidebar comment-section" style={{ position: 'sticky', top: 'clamp(60px,10vw,80px)' }}>
          <div style={{ background: C.panel, borderRadius: 'clamp(14px,3vw,18px)', border: `1px solid ${C.border}`, overflow: 'hidden', maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 'clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <h3 style={{ color: C.text, fontSize: 'clamp(13px,2.5vw,14px)', fontWeight: 800, margin: 0 }}>Discussion <span style={{ color: C.sub, fontWeight: 400, fontSize: 'clamp(12px,2vw,13px)', marginLeft: 6 }}>· {commentCount}</span></h3>
            </div>
            <div style={{ padding: 'clamp(8px,2vw,12px) clamp(10px,2vw,16px)', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              {replyTo && !isAnon && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isDark ? '#1a1a24' : '#f1f5f9', borderRadius: 9, padding: 'clamp(5px,1vw,7px) clamp(8px,2vw,11px)', marginBottom: 10 }}>
                  <span style={{ color: C.sub, fontSize: 'clamp(10px,2vw,12px)' }}>↩ Replying to <b style={{ color: '#e53935' }}>@{replyToUsername}</b></span>
                  <button onClick={() => { setReplyTo(null); setReplyToUsername('') }} style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 'clamp(13px,2.5vw,15px)', cursor: 'pointer', padding: 4, minWidth: 32, minHeight: 32 }}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 'clamp(6px,1.5vw,9px)', alignItems: 'flex-start' }}>
                <div style={{ marginTop: 3 }}><Avatar name={currentUser?.username || 'G'} size={28} bg={isAnon ? 'linear-gradient(135deg,#555,#333)' : undefined} /></div>
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea ref={commentInputRef} value={newComment} onChange={handleCommentInput} onFocus={() => setCommentFocused(true)} onBlur={() => setTimeout(() => setShowMentions(false), 150)} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleComment() } if (e.key === 'Escape') setShowMentions(false) }} placeholder={replyTo ? `Reply to @${replyToUsername}…` : isAnon ? 'Comment as Guest…' : 'Add a comment… · type @ to mention someone'} rows={commentFocused || newComment.length > 0 ? 3 : 1} style={{ width: '100%', padding: 'clamp(7px,1.5vw,9px) clamp(10px,2vw,12px)', background: commentFocused ? (isDark ? 'rgba(229,57,53,.06)' : 'rgba(229,57,53,.03)') : C.inputBg, border: commentFocused ? '1px solid rgba(229,57,53,.4)' : `1px solid ${C.border}`, borderRadius: 11, color: C.text, fontSize: 'clamp(12px,2vw,13px)', resize: 'none', outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit', transition: 'all .2s' }} />
                  {/* Mention dropdown (same) */}
                  {showMentions && mentionResults.length > 0 && (
                    <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, background: isDark ? '#111318' : '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,.4)' }}>
                      <div style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}` }}><span style={{ color: C.sub, fontSize: 10, fontWeight: 700, letterSpacing: '1px' }}>MENTION USER — @{mentionQuery}</span></div>
                      {mentionResults.map(u => (
                        <button key={u.id} onMouseDown={() => insertMention(u.username)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', border: 'none', cursor: 'pointer', background: 'transparent', textAlign: 'left' }} onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(229,57,53,.1)' : 'rgba(229,57,53,.06)' }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                          <div style={{ width: 28, height: 28, borderRadius: 14, flexShrink: 0, background: 'linear-gradient(135deg,#e53935,#8e24aa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff' }}>{u.username.charAt(0).toUpperCase()}</div>
                          <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>@{u.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {newComment.trim().length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 7 }}>
                      <span style={{ color: C.sub, fontSize: 'clamp(10px,1.8vw,11px)' }}>Ctrl+Enter to post</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => { setNewComment(''); setReplyTo(null); setReplyToUsername(''); setShowMentions(false) }} style={{ padding: 'clamp(5px,1vw,6px) clamp(10px,2vw,13px)', background: 'none', border: 'none', color: C.sub, fontSize: 'clamp(11px,2vw,13px)', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={handleComment} disabled={posting} style={{ padding: 'clamp(5px,1vw,7px) clamp(12px,2vw,16px)', borderRadius: 18, border: 'none', background: posting ? 'rgba(229,57,53,.5)' : 'linear-gradient(90deg,#e53935,#8e24aa)', color: '#fff', fontSize: 'clamp(11px,2vw,13px)', fontWeight: 700, cursor: posting ? 'not-allowed' : 'pointer' }}>{posting ? '…' : 'Post'}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: 'clamp(8px,2vw,12px) clamp(10px,2vw,16px)' }}>
              {comments.length === 0 ? <p style={{ color: C.sub, fontSize: 'clamp(11px,2vw,13px)', textAlign: 'center', padding: 'clamp(20px,4vw,28px) 0' }}>No comments yet. Be the first!</p>
                : comments.map(c => <CommentNode key={c.id} comment={c} depth={0} isDark={isDark} textColor={C.text} subColor={C.sub} borderColor={C.border} onReply={handleReply} />)
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}